/**
 * Storefront Module
 *
 * Handles storefront-specific functionality including:
 * - Reservation time slot loading and selection
 * - Gift card payment application
 * - Checkout functionality
 */

import { $, $$, ready } from '../utils/dom.js';
import { getToken } from '../utils/csrf.js';

// =============================================================================
// State
// =============================================================================

let appliedGiftCard = null;
let selectedTimeSlot = null;

// =============================================================================
// Reservation Booking
// =============================================================================

/**
 * Load available time slots for reservation
 */
export async function loadTimeSlots() {
    const dateInput = $('#reservation-date');
    const partySizeSelect = $('#party-size');
    const timeSlotsContainer = $('#time-slots-container');
    const restaurantId = window.restaurantId || dateInput?.dataset.restaurantId;

    if (!dateInput || !timeSlotsContainer) return;

    const date = dateInput.value;
    const partySize = partySizeSelect?.value || 2;

    if (!date) return;

    timeSlotsContainer.innerHTML = '<p class="col-span-full text-center text-gray-500 py-4">Loading available times...</p>';

    try {
        const response = await fetch(`/storefront/reservations/availability?restaurant_id=${restaurantId}&date=${date}&party_size=${partySize}`);
        const data = await response.json();

        if (data.success && data.slots && data.slots.length > 0) {
            renderTimeSlots(data.slots);
        } else {
            timeSlotsContainer.innerHTML = '<p class="col-span-full text-center text-gray-500 py-4">No available times for this date. Please try another date.</p>';
        }
    } catch (error) {
        console.error('Error loading time slots:', error);
        timeSlotsContainer.innerHTML = '<p class="col-span-full text-center text-red-500 py-4">Error loading available times. Please try again.</p>';
    }
}

/**
 * Render time slot buttons
 * @param {string[]} slots - Array of time strings
 */
function renderTimeSlots(slots) {
    const container = $('#time-slots-container');
    if (!container) return;

    container.innerHTML = '';

    slots.forEach(slot => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'time-slot px-4 py-2 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-colors';
        button.textContent = slot;
        button.onclick = () => selectTimeSlot(button, slot);
        container.appendChild(button);
    });
}

/**
 * Select a time slot
 * @param {HTMLElement} button - The clicked button
 * @param {string} time - The selected time
 */
export function selectTimeSlot(button, time) {
    // Remove selection from all slots
    $$('.time-slot').forEach(btn => {
        btn.classList.remove('bg-blue-500', 'text-white', 'border-blue-500');
        btn.classList.add('border-gray-300');
    });

    // Add selection to clicked slot
    button.classList.remove('border-gray-300');
    button.classList.add('bg-blue-500', 'text-white', 'border-blue-500');

    // Update hidden input
    const selectedTimeInput = $('#selected-time');
    if (selectedTimeInput) {
        selectedTimeInput.value = time;
    }

    selectedTimeSlot = time;

    // Enable submit button
    const submitBtn = $('#submit-btn');
    if (submitBtn) {
        submitBtn.disabled = false;
    }
}

// =============================================================================
// Gift Card Payment
// =============================================================================

/**
 * Show gift card error message
 * @param {string} message - Error message
 */
function showGiftCardError(message) {
    const errorDiv = $('#gift-card-error');
    if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.classList.remove('hidden');
    }
}

/**
 * Apply gift card to order
 */
export async function applyGiftCard() {
    const code = $('#gift_card_code')?.value.trim();
    const pin = $('#gift_card_pin')?.value.trim();
    const errorDiv = $('#gift-card-error');
    const applyBtn = $('#apply-gift-card-btn');

    // Clear previous errors
    if (errorDiv) errorDiv.classList.add('hidden');

    if (!code) {
        showGiftCardError('Please enter a gift card code');
        return;
    }

    // Get order total
    const orderTotalEl = $('#order-total');
    const orderTotal = parseFloat(orderTotalEl?.textContent.replace('$', '').replace(',', '')) || 0;

    // Show loading
    if (applyBtn) {
        applyBtn.disabled = true;
        applyBtn.textContent = 'Applying...';
    }

    try {
        const response = await fetch('/api/v1/checkout/apply-gift-card', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getToken()
            },
            body: JSON.stringify({
                code: code,
                pin: pin || null,
                order_total: orderTotal
            })
        });

        const data = await response.json();

        if (data.success) {
            appliedGiftCard = data.giftCard;
            updateGiftCardUI(data.giftCard, data.amountApplied);
        } else {
            showGiftCardError(data.message || 'Failed to apply gift card');
        }
    } catch (error) {
        console.error('Error applying gift card:', error);
        showGiftCardError('Error applying gift card. Please try again.');
    } finally {
        if (applyBtn) {
            applyBtn.disabled = false;
            applyBtn.textContent = 'Apply';
        }
    }
}

/**
 * Update UI after gift card is applied
 * @param {Object} giftCard - Applied gift card data
 * @param {number} amountApplied - Amount applied to order
 */
function updateGiftCardUI(giftCard, amountApplied) {
    const inputSection = $('#gift-card-input-section');
    const appliedSection = $('#gift-card-applied-section');
    const appliedAmount = $('#gift-card-applied-amount');
    const remainingBalance = $('#gift-card-remaining-balance');

    if (inputSection) inputSection.classList.add('hidden');
    if (appliedSection) appliedSection.classList.remove('hidden');

    if (appliedAmount) {
        appliedAmount.textContent = '$' + amountApplied.toFixed(2);
    }
    if (remainingBalance) {
        remainingBalance.textContent = '$' + (giftCard.balance - amountApplied).toFixed(2);
    }

    // Update order totals
    updateOrderTotals(amountApplied);
}

/**
 * Remove applied gift card
 */
export async function removeGiftCard() {
    if (!appliedGiftCard) return;

    try {
        await fetch('/api/v1/checkout/remove-gift-card', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': getToken()
            }
        });
    } catch (error) {
        console.error('Error removing gift card:', error);
    }

    appliedGiftCard = null;

    const inputSection = $('#gift-card-input-section');
    const appliedSection = $('#gift-card-applied-section');

    if (inputSection) inputSection.classList.remove('hidden');
    if (appliedSection) appliedSection.classList.add('hidden');

    // Clear inputs
    const codeInput = $('#gift_card_code');
    const pinInput = $('#gift_card_pin');
    if (codeInput) codeInput.value = '';
    if (pinInput) pinInput.value = '';

    // Reset order totals
    updateOrderTotals(0);
}

/**
 * Update order totals after gift card changes
 * @param {number} giftCardAmount - Amount from gift card
 */
function updateOrderTotals(giftCardAmount) {
    const giftCardRow = $('#gift-card-discount-row');
    const giftCardValue = $('#gift-card-discount-value');
    const newTotal = $('#order-total-final');

    if (giftCardAmount > 0) {
        if (giftCardRow) giftCardRow.classList.remove('hidden');
        if (giftCardValue) giftCardValue.textContent = '-$' + giftCardAmount.toFixed(2);
    } else {
        if (giftCardRow) giftCardRow.classList.add('hidden');
    }

    // Recalculate total (this would need the original subtotal)
    const subtotal = parseFloat($('#order-subtotal')?.textContent.replace('$', '').replace(',', '')) || 0;
    const tax = parseFloat($('#order-tax')?.textContent.replace('$', '').replace(',', '')) || 0;
    const total = subtotal + tax - giftCardAmount;

    if (newTotal) {
        newTotal.textContent = '$' + Math.max(0, total).toFixed(2);
    }
}

// =============================================================================
// Auto-Initialization
// =============================================================================

function initStorefront() {
    // Initialize reservation form
    const dateInput = $('#reservation-date');
    const partySizeSelect = $('#party-size');

    if (dateInput) {
        dateInput.addEventListener('change', loadTimeSlots);
    }
    if (partySizeSelect) {
        partySizeSelect.addEventListener('change', loadTimeSlots);
    }
}

ready(initStorefront);

// Expose globally for onclick handlers
window.loadTimeSlots = loadTimeSlots;
window.selectTimeSlot = selectTimeSlot;
window.applyGiftCard = applyGiftCard;
window.removeGiftCard = removeGiftCard;

export default {
    loadTimeSlots,
    selectTimeSlot,
    applyGiftCard,
    removeGiftCard
};
